import type { Position } from "../types/grid.js";
import type { ItemStack } from "../types/items.js";

/**
 * Loot-pile entity (I05).
 *
 * When an enemy dies and its loot table produced anything, a loot pile
 * is spawned on the enemy's cell. The pile is a pure data structure —
 * a positioned bag of `{ item, quantity }` stacks — plus a small set of
 * pure operations for "take one stack" / "take everything". Rendering
 * (sprite + glow) and walk-on/click dispatch are owned by the runtime
 * layer; tests only care that the state transitions are correct.
 *
 * Design notes:
 *   - Stacks stay in insertion order so UI listings remain stable.
 *   - `takeStack` returns the removed stack *and* the updated pile so
 *     callers can pipe straight into `CharacterInventory.addItem` and
 *     decide what to do with any `refused` quantity (put it back, drop
 *     it, show a toast).
 *   - A pile is considered "empty" when every stack has been taken.
 *     Callers (e.g. the dungeon builder) despawn the sprite on empty.
 */

/**
 * A stack inside a loot pile. Alias of the canonical {@link ItemStack}
 * — kept under the old name so call sites read like "pick a stack out
 * of this pile".
 */
export type LootPileStack = ItemStack;

export interface LootPile {
  readonly id: string;
  readonly position: Position;
  readonly stacks: readonly ItemStack[];
}

export function createLootPile(
  id: string,
  position: Position,
  stacks: readonly ItemStack[],
): LootPile {
  // Filter out empty stacks eagerly so "empty pile" stays a single rule.
  return { id, position, stacks: stacks.filter((s) => s.quantity > 0) };
}

export function isEmpty(pile: LootPile): boolean {
  return pile.stacks.length === 0;
}

/**
 * True if the pile contains at least one stack tagged "quest". The UI
 * uses this to apply the distinct glow called for in the spec.
 */
export function hasQuestItem(pile: LootPile): boolean {
  return pile.stacks.some((s) => s.item.tags.includes("quest"));
}

/**
 * Remove the stack at `index`. Returns `null` on out-of-range.
 * No-op `quantity` adjustment — piles yield whole stacks at a time.
 */
export function takeStack(
  pile: LootPile,
  index: number,
): { pile: LootPile; stack: ItemStack } | null {
  if (index < 0 || index >= pile.stacks.length) return null;
  const stack = pile.stacks[index]!;
  const nextStacks = [...pile.stacks.slice(0, index), ...pile.stacks.slice(index + 1)];
  return {
    pile: { ...pile, stacks: nextStacks },
    stack,
  };
}

/**
 * Remove the first stack whose item.id matches `itemId`. Returns `null`
 * if no such stack exists. Useful for "take named item" UI paths.
 */
export function takeItemById(
  pile: LootPile,
  itemId: string,
): { pile: LootPile; stack: ItemStack } | null {
  const idx = pile.stacks.findIndex((s) => s.item.id === itemId);
  return idx < 0 ? null : takeStack(pile, idx);
}

/**
 * Drain every stack. The returned `pile` is empty; `stacks` are the
 * items the caller now needs to add to one or more character
 * inventories. Order preserved.
 */
export function takeAll(pile: LootPile): {
  pile: LootPile;
  stacks: readonly ItemStack[];
} {
  return { pile: { ...pile, stacks: [] }, stacks: pile.stacks };
}

/**
 * Merge a stack back into the pile (e.g. when a character's inventory
 * refuses some of a stack due to capacity limits). Duplicate item IDs
 * are combined.
 */
export function returnStack(pile: LootPile, stack: ItemStack): LootPile {
  if (stack.quantity <= 0) return pile;
  const idx = pile.stacks.findIndex((s) => s.item.id === stack.item.id);
  if (idx < 0) {
    return { ...pile, stacks: [...pile.stacks, stack] };
  }
  const merged = [...pile.stacks];
  const existing = merged[idx]!;
  merged[idx] = { item: existing.item, quantity: existing.quantity + stack.quantity };
  return { ...pile, stacks: merged };
}
